import { Role } from '@zen/Auth/types';
import { PurchaseOrderTableVersion } from '@zen/Components/PurchaseOrderTableBanner';
import {
  analyticsRoutes,
  apiConsoleRoutes,
  bookingRequestRoutes,
  dashboardRoutes,
  financeRoutes,
  intakePlanningRoutes,
  orderRoutes,
  productRoutes,
  purchaseOrderRoutes,
  quoteRoutes,
  rateCardRoutes,
  shipmentRoutes,
  utilisationRoutes,
  warehouseIntakeRoutes
} from '@zen/Routes';
import financialsRoutes from '@zen/Routes/financials';

import type { SidebarLink } from '../types';
import type { CustomerLinksFeatureFlags } from './types';

export const getConfig = (
  canAccessApiConsole: boolean,
  canAccessFreightSpend: boolean,
  featureFlags: CustomerLinksFeatureFlags,
  role: Role,
  purchaseOrderTableVersion: PurchaseOrderTableVersion
): SidebarLink[] => {
  const isAgent: boolean = role === Role.AGENT_FORWARDER;
  const isManufacturer: boolean = role === Role.MANUFACTURER;
  const { isAskZenseiEnabled, isFinancialsEnabled, isFreightSpendEnabled, isRateCardsEnabled, isUtilisationEnabled } =
    featureFlags;
  const canAccessWarehousing: boolean = !isAgent && !isManufacturer;
  const isNewPurchaseOrderTableSelected: boolean = purchaseOrderTableVersion === PurchaseOrderTableVersion.NEW;
  const ordersUrl: string = isNewPurchaseOrderTableSelected ? purchaseOrderRoutes.index.getUrl() : orderRoutes.index.getUrl();

  return [
    {
      icon: 'zicon-dashboard',
      label: 'Dashboard',
      url: dashboardRoutes.dashboard.getUrl(),
      isExact: true
    },
    {
      icon: 'zicon-orders',
      label: 'Orders',
      url: ordersUrl
    },
    {
      icon: 'zicon-quotes',
      label: 'Quotes',
      url: quoteRoutes.quoteIndex.getUrl(),
      counter: 'quotesCount'
    },
    ...(isRateCardsEnabled
      ? [
          {
            icon: 'zicon-finance' as const,
            label: 'Rate cards',
            url: rateCardRoutes.index.getUrl()
          }
        ]
      : []),
    {
      icon: 'zicon-booking-request',
      label: 'Booking requests',
      url: bookingRequestRoutes.requestIndex.getUrl(),
      counter: 'bookingRequestsCount'
    },
    {
      icon: 'zicon-shipment',
      label: 'Shipments',
      url: shipmentRoutes.shipmentIndex.getUrl()
    },
    {
      icon: 'zicon-tag',
      label: 'Products',
      url: productRoutes.index.getUrl()
    },
    ...(canAccessApiConsole
      ? [
          {
            icon: 'zicon-api' as const,
            label: 'API console',
            url: apiConsoleRoutes.index.getUrl()
          }
        ]
      : []),
    {
      icon: 'zicon-intake-planning',
      label: 'Insights',
      url: '',
      children: [
        ...(isAskZenseiEnabled
          ? [
              {
                isHighlighted: true,
                label: 'Ask Luca',
                url: analyticsRoutes.conversationalAnalytics.getUrl()
              }
            ]
          : []),
        {
          label: 'Analytics',
          url: analyticsRoutes.index.getUrl()
        },
        ...(isFinancialsEnabled
          ? [
              {
                label: 'Financials',
                url: financialsRoutes.index.getUrl()
              }
            ]
          : []),
        {
          label: 'Intake planning',
          url: intakePlanningRoutes.index.getUrl()
        },
        ...(isUtilisationEnabled
          ? [
              {
                label: 'Utilisation',
                url: utilisationRoutes.index.getUrl()
              }
            ]
          : []),
        ...(isFreightSpendEnabled && canAccessFreightSpend
          ? [
              {
                label: 'Freight spend',
                url: financeRoutes.index.getUrl()
              }
            ]
          : []),
        ...(canAccessWarehousing
          ? [
              {
                label: 'Warehousing',
                url: warehouseIntakeRoutes.index.getUrl()
              }
            ]
          : [])
      ]
    }
  ];
};
